import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Product } from '../types';
import { productService } from '../services/productService';
import { useCart } from '../context/CartContext';
import { getProductImageUrl, hasProductImage } from '../utils/imageUtils';

const ProductDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { addToCart, getCartItem, updateQuantity, removeFromCart } = useCart();
    const [product, setProduct] = useState<Product | null>(null);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [imageError, setImageError] = useState(false);

    useEffect(() => {
        if (id) {
            loadProduct(parseInt(id));
        }
    }, [id]);

    const loadProduct = async (productId: number) => {
        try {
            setLoading(true);
            const response = await productService.getProductById(productId);
            setProduct(response.product);
        } catch (error) {
            console.error('Erreur lors du chargement du produit:', error);
            navigate('/catalog');
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(price);
    };

    const handleAddToCart = () => {
        if (product) {
            addToCart(product, quantity);
            alert(`${quantity} x ${product.name || product.designation} ajouté au panier!`);
        }
    };

    const handleQuantityChange = (change: number) => {
        const newQuantity = quantity + change;
        if (newQuantity >= 1) {
            setQuantity(newQuantity);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-eldai-blue text-xl">Chargement du produit...</div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-eldai-blue text-xl">Produit introuvable</div>
            </div>
        );
    }

    const cartItem = getCartItem(product.id);

    const handleCartIncrease = () => {
        if (cartItem) {
            updateQuantity(product.id, cartItem.quantity + 1);
        } else {
            addToCart(product, 1);
        }
    };

    const handleCartDecrease = () => {
        if (cartItem) {
            if (cartItem.quantity > 1) {
                updateQuantity(product.id, cartItem.quantity - 1);
            } else {
                removeFromCart(product.id);
            }
        }
    };

    const handleRemoveFromCart = () => {
        if (cartItem) {
            removeFromCart(product.id);
        }
    };

    return (
        <div className="min-h-screen bg-eldai-gray-light">
            <div className="container mx-auto px-4 py-8">
                {/* Breadcrumb */}
                <nav className="mb-8">
                    <div className="flex items-center space-x-2 text-sm text-eldai-gray">
                        <span
                            onClick={() => navigate('/')}
                            className="hover:text-eldai-blue cursor-pointer"
                        >
                            Accueil
                        </span>
                        <span>›</span>
                        <span
                            onClick={() => navigate('/catalog')}
                            className="hover:text-eldai-blue cursor-pointer"
                        >
                            Catalogue
                        </span>
                        <span>›</span>
                        {product.category_name && (
                            <>
                                <span
                                    onClick={() => navigate(`/catalog?categories=${product.category_id}`)}
                                    className="hover:text-eldai-blue cursor-pointer"
                                >
                                    {product.category_name}
                                </span>
                                <span>›</span>
                            </>
                        )}
                        <span className="text-eldai-blue">{product.name || product.designation}</span>
                    </div>
                </nav>

                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
                        {/* Product Image */}
                        <div className="space-y-4">
                            <div className="aspect-square bg-eldai-gray-light rounded-lg flex items-center justify-center overflow-hidden">
                                {(() => {
                                    if (imageError) {
                                        return <div className="text-eldai-gray text-6xl">📦</div>;
                                    }

                                    // Try S3 image first if we have a product code
                                    if (hasProductImage(product)) {
                                        return (
                                            <img
                                                src={getProductImageUrl(product.code!)}
                                                alt={product.name || product.designation}
                                                className="w-full h-full object-cover"
                                                onError={() => setImageError(true)}
                                            />
                                        );
                                    }

                                    // Fallback to existing image_url
                                    if (product.image_url) {
                                        return (
                                            <img
                                                src={product.image_url}
                                                alt={product.name || product.designation}
                                                className="w-full h-full object-cover"
                                                onError={() => setImageError(true)}
                                            />
                                        );
                                    }

                                    return <div className="text-eldai-gray text-6xl">📦</div>;
                                })()}
                            </div>
                        </div>

                        {/* Product Info */}
                        <div className="space-y-6">
                            <div>
                                <h1 className="text-3xl font-bold text-eldai-blue mb-2">
                                    {product.name || product.designation}
                                </h1>
                                {product.category_name && (
                                    <p className="text-eldai-gray">
                                        Catégorie: {product.category_name}
                                    </p>
                                )}
                                {product.code && (
                                    <p className="text-sm text-eldai-gray">
                                        Référence: {product.code}
                                    </p>
                                )}
                            </div>

                            {/* Price */}
                            <div className="text-4xl font-bold text-eldai-blue">
                                {formatPrice(parseFloat((product.unit_price || product.price || 0).toString()))}
                                {product.unit && (
                                    <span className="text-lg text-eldai-gray ml-2">
                                        / {product.unit}
                                    </span>
                                )}
                                {product.pcb && product.pcb > 1 && (
                                    <div className="text-sm text-eldai-gray mt-1">
                                        Pack de {product.pcb} unités
                                    </div>
                                )}
                            </div>

                            {/* Kosher Certification */}
                            {product.kosher_certification && (
                                <div className="bg-eldai-yellow text-eldai-blue px-4 py-2 rounded-lg inline-block">
                                    <strong>✓ Certifié Casher:</strong> {product.kosher_certification}
                                </div>
                            )}

                            {/* Description */}
                            {product.description && (
                                <div>
                                    <h3 className="text-lg font-semibold text-eldai-blue mb-2">
                                        Description
                                    </h3>
                                    <p className="text-eldai-gray leading-relaxed">
                                        {product.description}
                                    </p>
                                </div>
                            )}

                            {/* Availability Status */}
                            <div className="border-t pt-4">
                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-eldai-gray">Disponibilité:</span>
                                    <span className={`font-semibold ${product.is_available ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                        {product.is_available
                                            ? 'Disponible'
                                            : 'Indisponible'
                                        }
                                    </span>
                                </div>

                                {/* Quantity and Add to Cart / Cart Controls */}
                                {product.is_available && (
                                    <div className="space-y-4">
                                        {!cartItem ? (
                                            // Show quantity selector and add to cart if not in cart
                                            <>
                                                <div className="flex items-center space-x-4">
                                                    <span className="text-eldai-gray">Quantité:</span>
                                                    <div className="flex items-center border border-eldai-gray rounded-lg">
                                                        <button
                                                            onClick={() => handleQuantityChange(-1)}
                                                            disabled={quantity <= 1}
                                                            className="px-3 py-2 hover:bg-eldai-gray-light disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            −
                                                        </button>
                                                        <span className="px-4 py-2 border-x border-eldai-gray">
                                                            {quantity}
                                                        </span>
                                                        <button
                                                            onClick={() => handleQuantityChange(1)}
                                                            className="px-3 py-2 hover:bg-eldai-gray-light"
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={handleAddToCart}
                                                    className="w-full bg-eldai-yellow text-eldai-blue py-3 px-6 rounded-lg font-semibold hover:bg-eldai-blue hover:text-white transition-colors text-lg"
                                                >
                                                    Ajouter au panier - {formatPrice(parseFloat((product.unit_price || product.price || 0).toString()) * quantity)}
                                                </button>
                                            </>
                                        ) : (
                                            // Show cart controls if already in cart
                                            <div className="space-y-4">
                                                <div className="bg-eldai-yellow/20 border-2 border-eldai-yellow rounded-lg p-4">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <span className="text-eldai-blue font-semibold">Dans le panier:</span>
                                                        <span className="text-eldai-blue font-bold text-lg">{cartItem.quantity} unité{cartItem.quantity > 1 ? 's' : ''}</span>
                                                    </div>

                                                    <div className="flex items-center justify-center space-x-4 mb-3">
                                                        <button
                                                            onClick={handleCartDecrease}
                                                            className="w-10 h-10 bg-eldai-yellow hover:bg-eldai-blue hover:text-white text-eldai-blue rounded-full flex items-center justify-center font-bold transition-colors"
                                                        >
                                                            −
                                                        </button>
                                                        <span className="font-bold text-eldai-blue text-xl min-w-[3rem] text-center">
                                                            {cartItem.quantity}
                                                        </span>
                                                        <button
                                                            onClick={handleCartIncrease}
                                                            className="w-10 h-10 bg-eldai-yellow hover:bg-eldai-blue hover:text-white text-eldai-blue rounded-full flex items-center justify-center font-bold transition-colors"
                                                        >
                                                            +
                                                        </button>
                                                    </div>

                                                    <div className="text-center text-eldai-gray text-sm mb-3">
                                                        Total: {formatPrice(parseFloat((product.unit_price || product.price || 0).toString()) * cartItem.quantity)}
                                                    </div>

                                                    <button
                                                        onClick={handleRemoveFromCart}
                                                        className="w-full bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg font-semibold transition-colors"
                                                    >
                                                        Retirer du panier
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {product.is_available === false && (
                                    <button
                                        disabled
                                        className="w-full bg-eldai-gray text-white py-3 px-6 rounded-lg font-semibold cursor-not-allowed text-lg"
                                    >
                                        Produit indisponible
                                    </button>
                                )}
                            </div>

                            {/* Additional Info */}
                            <div className="border-t pt-4 space-y-2 text-sm text-eldai-gray">
                                {product.pcb && product.pcb > 1 && (
                                    <div className="flex justify-between">
                                        <span>Unités par pack:</span>
                                        <span>{product.pcb}</span>
                                    </div>
                                )}
                                {product.weight && (
                                    <div className="flex justify-between">
                                        <span>Poids:</span>
                                        <span>{product.weight}</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span>Ajouté le:</span>
                                    <span>
                                        {product.created_at && new Date(product.created_at).toLocaleDateString('fr-FR')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Back to Catalog */}
                <div className="mt-8 text-center">
                    <button
                        onClick={() => navigate('/catalog')}
                        className="bg-eldai-blue text-white px-6 py-2 rounded-lg hover:bg-eldai-light-blue transition-colors"
                    >
                        ← Retour au catalogue
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProductDetailsPage;
